import React, { useEffect, useState } from "react";

const PaiementTable = ({
    paiements = [],
    loading = false,
    onEdit,
    onDelete
}) => {

    // =====================================================
    // PAGINATION
    // =====================================================

    const [currentPage, setCurrentPage] = useState(1);

    const itemsPerPage = 10;


    // =====================================================
    // RESET PAGINATION
    // =====================================================

    useEffect(() => {

        setCurrentPage(1);

    }, [paiements]);


    // =====================================================
    // FORMAT DATE
    // =====================================================

    const formatDate = (date) => {

        if (!date) {
            return "-";
        }

        const d = new Date(date);

        if (Number.isNaN(d.getTime())) {
            return "-";
        }

        return d.toLocaleDateString("fr-FR");
    };


    // =====================================================
    // FORMAT HEURE
    // =====================================================

    const formatHeure = (heure) => {

        if (!heure) {
            return "-";
        }

        return String(heure).substring(0, 5);
    };


    // =====================================================
    // FORMAT MONTANT
    // =====================================================

    const formatMontant = (montant) => {

        if (
            montant === null ||
            montant === undefined ||
            montant === ""
        ) {
            return "0,00 CAD";
        }

        const valeur = Number(montant);

        if (Number.isNaN(valeur)) {
            return "0,00 CAD";
        }

        return valeur.toLocaleString(
            "fr-FR",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        ) + " CAD";
    };


    // =====================================================
    // STATUT
    // =====================================================

    const getStatutClass = (statut) => {

        switch (String(statut || "").toLowerCase()) {

            case "payé":
            case "paye":
                return "statut-paye";

            case "partiel":
                return "statut-partiel";

            case "annulé":
            case "annule":
                return "statut-annule";

            default:
                return "statut-default";
        }
    };


    // =====================================================
    // LOADING
    // =====================================================

    if (loading) {

        return (
            <div className="paiement-table-loading">
                Chargement des paiements...
            </div>
        );
    }


    // =====================================================
    // TABLEAU VIDE
    // =====================================================

    if (!Array.isArray(paiements) || paiements.length === 0) {

        return (
            <div className="paiement-table-empty">

                <div className="empty-icon">
                    💳
                </div>

                <h3>
                    Aucun paiement
                </h3>

                <p>
                    Aucun paiement n'a été enregistré
                    pour le moment.
                </p>

            </div>
        );
    }


    // =====================================================
    // PAGINATION
    // =====================================================

    const totalItems = paiements.length;

    const totalPages = Math.ceil(
        totalItems / itemsPerPage
    );

    const startIndex =
        (currentPage - 1) * itemsPerPage;

    const endIndex =
        startIndex + itemsPerPage;

    const paiementsPage =
        paiements.slice(
            startIndex,
            endIndex
        );


    // =====================================================
    // CHANGER PAGE
    // =====================================================

    const changerPage = (page) => {

        if (
            page < 1 ||
            page > totalPages
        ) {
            return;
        }

        setCurrentPage(page);

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };


    // =====================================================
    // NUMÉROS DE PAGES
    // =====================================================

    const getPages = () => {

        const pages = [];

        /*
         * Si peu de pages :
         * afficher toutes les pages.
         */

        if (totalPages <= 7) {

            for (
                let page = 1;
                page <= totalPages;
                page++
            ) {
                pages.push(page);
            }

            return pages;
        }


        /*
         * Première page
         */

        pages.push(1);


        /*
         * Pages autour de la page actuelle
         */

        if (currentPage > 3) {
            pages.push("...");
        }


        const start = Math.max(
            2,
            currentPage - 1
        );

        const end = Math.min(
            totalPages - 1,
            currentPage + 1
        );


        for (
            let page = start;
            page <= end;
            page++
        ) {

            pages.push(page);

        }


        /*
         * Dernières pages
         */

        if (currentPage < totalPages - 2) {
            pages.push("...");
        }


        pages.push(totalPages);


        return pages;
    };


    // =====================================================
    // INFORMATIONS PAGINATION
    // =====================================================

    const premierElement =
        startIndex + 1;

    const dernierElement =
        Math.min(
            endIndex,
            totalItems
        );


    // =====================================================
    // RENDER
    // =====================================================

    return (

        <div className="paiement-table-container">


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="paiement-table-header">

                <div>

                    <h2>
                        Liste des paiements
                    </h2>

                    <span>

                        {totalItems} paiement
                        {totalItems > 1 ? "s" : ""}

                    </span>

                </div>

            </div>


            {/* =================================================
                TABLEAU DESKTOP
            ================================================= */}

            <div className="table-responsive">

                <table className="paiement-table">

                    <thead>

                        <tr>

                            <th>
                                Rendez-vous
                            </th>

                            <th>
                                Client
                            </th>

                            <th>
                                Téléphone
                            </th>

                            <th>
                                Date RDV
                            </th>

                            <th>
                                Heure
                            </th>

                            <th>
                                Montant
                            </th>

                            <th>
                                Mode de paiement
                            </th>

                            <th>
                                Statut
                            </th>

                            <th>
                                Date paiement
                            </th>

                            <th>
                                Actions
                            </th>

                        </tr>

                    </thead>


                    <tbody>

                        {paiementsPage.map(
                            (paiement) => (

                                <tr
                                    key={paiement.id}
                                >

                                    {/* RDV */}

                                    <td>

                                        <div className="rdv-info">

                                            <strong>
                                                RDV #
                                                {
                                                    paiement.rendez_vous_id
                                                }
                                            </strong>

                                        </div>

                                    </td>


                                    {/* CLIENT */}

                                    <td>

                                        <div className="client-info">

                                            <strong>
                                                {
                                                    paiement.client_nom
                                                    || "-"
                                                }
                                            </strong>

                                        </div>

                                    </td>


                                    {/* TELEPHONE */}

                                    <td>

                                        {
                                            paiement.client_telephone
                                            || "-"
                                        }

                                    </td>


                                    {/* DATE RDV */}

                                    <td>

                                        {
                                            formatDate(
                                                paiement.date_rdv
                                            )
                                        }

                                    </td>


                                    {/* HEURE */}

                                    <td>

                                        {
                                            formatHeure(
                                                paiement.heure_debut
                                            )
                                        }

                                        {" - "}

                                        {
                                            formatHeure(
                                                paiement.heure_fin
                                            )
                                        }

                                    </td>


                                    {/* MONTANT */}

                                    <td>

                                        <strong
                                            className="paiement-montant"
                                        >

                                            {
                                                formatMontant(
                                                    paiement.montant
                                                )
                                            }

                                        </strong>

                                    </td>


                                    {/* MODE */}

                                    <td>

                                        <span className="mode-paiement">

                                            <span>
                                                💳
                                            </span>

                                            <span>
                                                {
                                                    paiement.mode_paiement
                                                    || "-"
                                                }
                                            </span>

                                        </span>

                                    </td>


                                    {/* STATUT */}

                                    <td>

                                        <span
                                            className={
                                                `statut-paiement ${
                                                    getStatutClass(
                                                        paiement.statut
                                                    )
                                                }`
                                            }
                                        >

                                            {
                                                paiement.statut
                                                || "-"
                                            }

                                        </span>

                                    </td>


                                    {/* DATE PAIEMENT */}

                                    <td>

                                        {
                                            formatDate(
                                                paiement.date_paiement
                                            )
                                        }

                                    </td>


                                    {/* ACTIONS */}

                                    <td>

                                        <div className="paiement-actions">

                                            <button
                                                type="button"
                                                className="btn-edit"
                                                title="Modifier"
                                                onClick={() =>
                                                    onEdit &&
                                                    onEdit(
                                                        paiement
                                                    )
                                                }
                                            >
                                                ✏️
                                            </button>


                                            <button
                                                type="button"
                                                className="btn-delete"
                                                title="Supprimer"
                                                onClick={() =>
                                                    onDelete &&
                                                    onDelete(
                                                        paiement.id
                                                    )
                                                }
                                            >
                                                🗑️
                                            </button>

                                        </div>

                                    </td>

                                </tr>

                            )
                        )}

                    </tbody>

                </table>

            </div>


            {/* =================================================
                VERSION MOBILE
            ================================================= */}

            <div className="paiement-mobile-list">

                {paiementsPage.map(
                    (paiement) => (

                        <div
                            className="paiement-mobile-card"
                            key={paiement.id}
                        >

                            {/* HEADER */}

                            <div className="paiement-mobile-header">

                                <div>

                                    <strong>
                                        RDV #
                                        {
                                            paiement.rendez_vous_id
                                        }
                                    </strong>

                                    <small>
                                        {
                                            formatDate(
                                                paiement.date_rdv
                                            )
                                        }
                                    </small>

                                </div>


                                <span
                                    className={
                                        `statut-paiement ${
                                            getStatutClass(
                                                paiement.statut
                                            )
                                        }`
                                    }
                                >

                                    {
                                        paiement.statut
                                        || "-"
                                    }

                                </span>

                            </div>


                            {/* CLIENT */}

                            <div className="paiement-mobile-row">

                                <span className="mobile-label">
                                    Client
                                </span>

                                <strong>
                                    {
                                        paiement.client_nom
                                        || "-"
                                    }
                                </strong>

                            </div>


                            {/* TELEPHONE */}

                            <div className="paiement-mobile-row">

                                <span className="mobile-label">
                                    Téléphone
                                </span>

                                <span>
                                    {
                                        paiement.client_telephone
                                        || "-"
                                    }
                                </span>

                            </div>


                            {/* HEURE */}

                            <div className="paiement-mobile-row">

                                <span className="mobile-label">
                                    Heure
                                </span>

                                <span>

                                    {
                                        formatHeure(
                                            paiement.heure_debut
                                        )
                                    }

                                    {" - "}

                                    {
                                        formatHeure(
                                            paiement.heure_fin
                                        )
                                    }

                                </span>

                            </div>


                            {/* MONTANT */}

                            <div className="paiement-mobile-row">

                                <span className="mobile-label">
                                    Montant
                                </span>

                                <strong
                                    className="paiement-montant"
                                >
                                    {
                                        formatMontant(
                                            paiement.montant
                                        )
                                    }
                                </strong>

                            </div>


                            {/* MODE */}

                            <div className="paiement-mobile-row">

                                <span className="mobile-label">
                                    Mode
                                </span>

                                <span>

                                    💳{" "}
                                    {
                                        paiement.mode_paiement
                                        || "-"
                                    }

                                </span>

                            </div>


                            {/* DATE PAIEMENT */}

                            <div className="paiement-mobile-row">

                                <span className="mobile-label">
                                    Date paiement
                                </span>

                                <span>
                                    {
                                        formatDate(
                                            paiement.date_paiement
                                        )
                                    }
                                </span>

                            </div>


                            {/* ACTIONS */}

                            <div className="paiement-mobile-actions">

                                <button
                                    type="button"
                                    className="btn-edit"
                                    onClick={() =>
                                        onEdit &&
                                        onEdit(paiement)
                                    }
                                >
                                    ✏️ Modifier
                                </button>


                                <button
                                    type="button"
                                    className="btn-delete"
                                    onClick={() =>
                                        onDelete &&
                                        onDelete(paiement.id)
                                    }
                                >
                                    🗑️ Supprimer
                                </button>

                            </div>

                        </div>

                    )
                )}

            </div>


            {/* =================================================
                PAGINATION
            ================================================= */}

            {totalPages > 1 && (

                <div className="paiement-pagination">


                    {/* INFORMATIONS */}

                    <div className="pagination-info">

                        Affichage de{" "}

                        <strong>
                            {premierElement}
                        </strong>

                        {" à "}

                        <strong>
                            {dernierElement}
                        </strong>

                        {" sur "}

                        <strong>
                            {totalItems}
                        </strong>

                        {" paiements"}

                    </div>


                    {/* CONTROLES */}

                    <div className="pagination-controls">


                        {/* PRECEDENT */}

                        <button
                            type="button"
                            className="pagination-btn"
                            disabled={
                                currentPage === 1
                            }
                            onClick={() =>
                                changerPage(
                                    currentPage - 1
                                )
                            }
                        >

                            ‹

                            <span>
                                Précédent
                            </span>

                        </button>


                        {/* PAGES */}

                        <div className="pagination-pages">

                            {getPages().map(
                                (page, index) => (

                                    page === "..."

                                        ? (

                                            <span
                                                key={`dots-${index}`}
                                                className="pagination-dots"
                                            >
                                                ...
                                            </span>

                                        )

                                        : (

                                            <button
                                                key={page}
                                                type="button"
                                                className={
                                                    `pagination-page ${
                                                        currentPage === page
                                                            ? "active"
                                                            : ""
                                                    }`
                                                }
                                                onClick={() =>
                                                    changerPage(
                                                        page
                                                    )
                                                }
                                            >

                                                {page}

                                            </button>

                                        )

                                )
                            )}

                        </div>


                        {/* SUIVANT */}

                        <button
                            type="button"
                            className="pagination-btn"
                            disabled={
                                currentPage === totalPages
                            }
                            onClick={() =>
                                changerPage(
                                    currentPage + 1
                                )
                            }
                        >

                            <span>
                                Suivant
                            </span>

                            ›

                        </button>

                    </div>

                </div>

            )}

        </div>
    );
};


export default PaiementTable;